import type { JSX, PropsWithChildren, Ref } from "react";

import type { ScrollViewRef } from "@/ui/layout/ScrollView";
import { isWeb } from "@/utils/platform.util";

enum ModalLayoutDimensions {
  HeaderHeight = 70,
}

type ModalVariant = "normal" | "oversized";

interface ModalLayoutProps extends PropsWithChildren {
  isListView?: boolean;
  isPaddedHorizontally?: boolean;
  // web-only: invoked when the dimmed backdrop behind the modal card is clicked, defaults to router.dismiss()
  onBackdropPress?: () => void;
  renderFloatingButton?: () => JSX.Element | null;
  renderFooter?: () => JSX.Element | undefined;
  renderHeader?: () => JSX.Element;
  scrollViewRef?: Ref<ScrollViewRef>;
  // web-only: opt out of click-outside-to-close (eg for assessment-webview, recommended-group-class)
  shouldBackdropPressDismiss?: boolean;
  // web-only: "oversized" grows the modal card to 80% of the viewport instead of the default 800x600 cap (eg for document/webview previews)
  variant?: ModalVariant;
}

interface WebModalConstraintsProps {
  normal: {
    borderRadius?: number;
    maxHeight?: number;
    maxWidth?: number;
    overflow?: "hidden";
    width?: `${number}%`;
  };
  oversized: {
    borderRadius?: number;
    maxHeight?: `${number}%`;
    maxWidth?: `${number}%`;
    overflow?: "hidden";
    width?: `${number}%`;
  };
}

const WebModalConstraints: WebModalConstraintsProps = isWeb()
  ? {
      normal: {
        borderRadius: 24,
        maxHeight: 600,
        maxWidth: 800,
        overflow: "hidden",
        width: "100%",
      },
      oversized: {
        borderRadius: 24,
        maxHeight: "80%",
        maxWidth: "80%",
        overflow: "hidden",
        width: "80%",
      },
    }
  : { normal: {}, oversized: {} };

export { ModalLayoutDimensions, WebModalConstraints };
export type { ModalLayoutProps };
